import domready from 'domready';
import React, { lazy, Suspense } from 'react';
import { render } from 'react-dom';
import { Provider } from 'react-redux';

import { createIntl } from 'react-intl';
import { IntlProvider } from 'react-intl-redux';

import { BrowserRouter, Route, Switch } from 'react-router-dom';
import Logger from './helper/Logger';
import debug from 'debug';
import RoomClient from './helper/mediasoup/RoomClient';
import NetPlaContext from './NetPlaContext';
import { MuiThemeProvider, createTheme } from '@material-ui/core/styles';
import { PersistGate } from 'redux-persist/lib/integration/react';
import { persistor, store } from './stores/store';
import * as serviceWorker from './serviceWorker';
import { themeConfig } from './commons/config';

import './index.css';
import { deviceInfo } from './helper/deviceInfo';
import {
	AUTH_PATH,
	USER_JOIN,
	OAUTH_CALL_BACK,
	HOME_PATH, PRICING,
	USER_PROFILE,
	USER_PRIVATE_ROOMS,
	USER_PUBLIC_ROOMS,
	USERS,
	USER_SETTING,
	ADMIN_STATISTICS,
	ADMIN_PRICING,
	ADMIN_USERS,
	ADMIN_SUBSCRIPTIONS,
	VERIFY_PATH,
	ADD_PAYMENT_METHOD,
	CONFIRMATION_CHANGE_EMAIL_PATH,
	RESET_PASS_PATH,
	DISCUSSON_PATH,
	JOIN_WITH_INVITATION,
	RECORDING_JOIN,
	USER_RECORDINGS,
	ACCOUNT_PRICING,
	NETPLA_PRICING,
	SUBSCRIPTION,
	VERIFY_AUTH,
	USER_EDIT,
	PRIVACY_POLICY,
	TERMS_AND_CONDITION,
	WEBINAR_PATH,
	USER_CONTACT_US,
	ADMIN_PARTICIPANTS,
	EMAIL_CHANGE_VERIFY,
	USER_PAYMENT_METHODS,
	ADMIN_SUBSCRIPTION_HISTORY,
	ADMIN_RENEWAL_MGMT
} from './helper/routes';
import NetplaSocket from './helper/socket';
import LoadingView from './components/LoadingView/LoadingView';
import PaymentMethods from './screens/Dashboard/PaymentMethods/PaymentMethods';
import StripeWrapper from './screens/StripeWrapper';

const MainLayout = lazy(() => import('./components/Layout/MainLayout'));
const DashboardLayout = lazy(() => import('./components/Layout/DashboardLayout'));
const Profile = lazy(() => import('./screens/Dashboard/Profile/Profile'));
const Rooms = lazy(() => import('./screens/Dashboard/Rooms/Rooms'));
const Pricing = lazy(() => import('./screens/Dashboard/Pricing/Pricing'));
const GlobalPricing = lazy(() => import('./screens/GlobalPricing/GlobalPricing'));
const Settings = lazy(() => import('./screens/Dashboard/Settings/Settings'));
const Authentication = lazy(() => import('./screens/Authentication'));
const InvitationJoin = lazy(() => import('./screens/Authentication/InvitationJoin/InvitationJoin'));
const MeetingRoom = lazy(() => import('./screens/Dashboard/Rooms/MeetingRoom'));
const AdminStatistics = lazy(() => import('./screens/Admin/Statistics/Statistics'));
const AdminSubscriptions = lazy(() => import('./screens/Admin/Subscriptions/Subscriptions'));
const AdminPricing = lazy(() => import('./screens/Admin/Pricing/Pricing'));
const AdminUsers = lazy(() => import('./screens/Admin/Users/Users'));
const Participants = lazy(() => import('./screens/Admin/Participants/Participants'));
const SubscriptionHistory = lazy(() => import('./screens/Admin/SubscriptionHistory/SubscriptionHistory'));
const RenewalMgmt = lazy(() => import('./screens/Admin/RenewalMgmt/RenewalMgmt'));
const EditUser = lazy(() => import('./screens/Dashboard/EditUser/EditUser'));
const ContactUs = lazy(() => import('./screens/Dashboard/ContactUs/ContactUs'));
const AuthCallback = lazy(() => import('./screens/Authentication/AuthCallback/AuthCallback'));
const AddPaymentMethod = lazy(() => import('./screens/Authentication/AddPaymentMethod/AddPaymentMethod'));
const TwoStepAuth = lazy(() => import('./screens/Authentication/TwoStepAuth/TwoStepAuth'));
const EmailVerification = lazy(() => import('./screens/Authentication/EmailVerification/EmailVerification'));
const ResetPassForm = lazy(() => import('./screens/Authentication/ResetPassForm/ResetPassForm'));
const EmailChangeVerifyForm = lazy(() => import('./screens/Authentication/EmailChangeVerifyForm/EmailChangeVerifyForm'));
const InvitationUser = lazy(() => import('./screens/InvitationUser/InvitationUser'));
const RecordingJoin = lazy(() => import('./screens/RecordingJoin/RecordingJoin'));
const RecordingFiles = lazy(() => import('./screens/Dashboard/Rooms/RecordingFiles/RecordingFiles'));
const Subscription = lazy(() => import('./screens/Subscription/Subscription'));
const PrivacyPolicy = lazy(() => import('./screens/PrivacyPolicy/PrivacyPolicy'));
const TermsAndCondition = lazy(() => import('./screens/TermsAndCondition/TermsAndCondition'));
const JoinNow = lazy(() => import('./screens/JoinNow'));

const intl = createIntl();

debug.enable('* -engine* -socket* -RIE* *WARN* *ERROR*');

const logger = new Logger();

let roomClient;

let netplaClient;

NetplaSocket.init({ store });
RoomClient.init({ store });

const theme = createTheme(themeConfig);

const Router = BrowserRouter;

domready(() =>
{
	logger.debug('DOM ready');

	run();
});

function run()
{
	logger.debug('run() [environment:%s]', process.env.NODE_ENV);
	// Get current device.
	const device = deviceInfo();

	netplaClient = new NetplaSocket();
	roomClient = new RoomClient(device);

	render(
		<StripeWrapper>
			<Provider store={store}>
				<MuiThemeProvider theme={theme}>
					<IntlProvider value={intl}>
						<PersistGate persistor={persistor}>
							<NetPlaContext.Provider value={{ roomClient, netplaClient }} >
								<Router>
									<Suspense fallback={<LoadingView/>}>
										<MainLayout>
											<Switch>
												<Route exact path={VERIFY_PATH} component={EmailVerification} />
												<Route exact path={CONFIRMATION_CHANGE_EMAIL_PATH} component={EmailVerification} />
												<Route exact path={AUTH_PATH} component={Authentication} />
												<Route exact path={VERIFY_AUTH} component={TwoStepAuth} />
												<Route exact path={JOIN_WITH_INVITATION} component={InvitationJoin} />
												<Route exact path={RECORDING_JOIN} component={RecordingJoin} />
												<Route exact path={RESET_PASS_PATH} component={ResetPassForm} />
												<Route exact path={EMAIL_CHANGE_VERIFY} component={EmailChangeVerifyForm} />
												<Route exact path={HOME_PATH} component={JoinNow} />
												<Route exact path={NETPLA_PRICING} component={GlobalPricing} />
												<Route exact path={PRIVACY_POLICY} component={PrivacyPolicy} />
												<Route exact path={TERMS_AND_CONDITION} component={TermsAndCondition} />
												<Route exact path={USER_JOIN} component={InvitationUser} />
												<Route exact path={OAUTH_CALL_BACK} component={AuthCallback} />
												<Route exact path={DISCUSSON_PATH} component={MeetingRoom} />
												<Route exact path={WEBINAR_PATH} component={MeetingRoom} />
												<Route exact path={ACCOUNT_PRICING} component={Pricing} />
												<Route exact path={SUBSCRIPTION} component={Subscription} />
												<Route exact path={ADD_PAYMENT_METHOD} component={AddPaymentMethod} />

												<DashboardLayout>
													{/* USER ROUTES */}
													<Route exact path={USER_PROFILE} component={Profile} />
													<Route exact path={USER_PUBLIC_ROOMS} component={Rooms} />
													<Route exact path={USER_PRIVATE_ROOMS} component={Rooms} />
													<Route exact path={USER_SETTING} component={Settings} />
													<Route exact path={USER_PAYMENT_METHODS} component={PaymentMethods} />
													<Route exact path={USERS} component={AdminUsers} />
													<Route exact path={USER_EDIT} component={EditUser} />
													<Route exact path={USER_CONTACT_US} component={ContactUs} />
													<Route exact path={USER_RECORDINGS} component={RecordingFiles} />
													<Route exact path={PRICING} component={Pricing} />
													{/* ADMIN ROUTES */}
													<Route exact path={ADMIN_STATISTICS} component={AdminStatistics} />
													<Route exact path={ADMIN_PRICING} component={AdminPricing} />
													<Route exact path={ADMIN_USERS} component={AdminUsers} />
													<Route exact path={ADMIN_SUBSCRIPTIONS} component={AdminSubscriptions} />
													<Route exact path={ADMIN_SUBSCRIPTION_HISTORY} component={SubscriptionHistory} />
													<Route exact path={ADMIN_RENEWAL_MGMT} component={RenewalMgmt} />
													<Route exact path={ADMIN_PARTICIPANTS} component={Participants} />
												</DashboardLayout>
											</Switch>
										</MainLayout>
									</Suspense>
								</Router>
							</NetPlaContext.Provider>
						</PersistGate>
					</IntlProvider>
				</MuiThemeProvider>
			</Provider>
		</StripeWrapper>,
		document.getElementById('NetPlaLive')
	);
}

serviceWorker.unregister();
